import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { IDashboardItemFactory } from 'src/app/utils/dashboard-item';
import {
    IStringInputFilterDatasourceColumnDashboardItem,
    IStringInputFilterDatasourceColumnState,
} from './string-input';
import {
    getDatasourceColumnName,
    ICompiledSQLTokens,
} from 'src/app/utils/datasource-sql-map/datasource';
import {
    QueryContextChange,
    QueryContextService,
} from 'src/app/services/query-context/query-context.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import {
    IQueryContextChange,
    IQueryContextDatasourceColumnState,
} from 'src/app/services/query-context/models/IQueryContext';
import { S } from '@angular/cdk/keycodes';

@Component({
    selector: 'app-string-input-filter',
    templateUrl: './string-input-filter.component.html',
    styleUrls: ['./string-input-filter.component.scss'],
})
export class StringInputFilterDashboardItemComponent {
    @Input()
    dashboardItemFactory!: IDashboardItemFactory;
    dashboardItem!: IStringInputFilterDatasourceColumnDashboardItem;
    state: IStringInputFilterDatasourceColumnState =
        StringInputFilterDashboardItemComponent.getDefaultState();

    value: string = '';

    datasourceColumnLabel: string = '';

    constructor(
        private queryContextService: QueryContextService,
        private logger: LoggerService // private cd: ChangeDetectorRef
    ) {}
    ngOnInit() {
        this.dashboardItem =
            this.dashboardItemFactory.create() as IStringInputFilterDatasourceColumnDashboardItem;
        this.datasourceColumnLabel = this.getColumnLabel();

        this.queryContextService.appliedQueryContextEmitter.subscribe(
            this.queryContextSubscriber
        );
    }

    public getColumnLabel() {
        return (
            this.dashboardItem?.datasourceColumnLabel ??
            getDatasourceColumnName(this.dashboardItem.datasourceColumnId) ??
            this.dashboardItem.datasourceColumnId
        );
    }

    /** React to changes in the query context */
    queryContextSubscriber = (contextChange: QueryContextChange) => {
        if (contextChange.reason === 'InitialContext') {
            // find in contextChange.context.datasourceColumns item with the same id as this.datasourceColumnId
            const datasourceColumn =
                contextChange.context.datasourceColumns.find((item) => {
                    return item.id === this.dashboardItem.datasourceColumnId;
                });
            // set provided state or default state
            const state = datasourceColumn
                ? datasourceColumn.state
                : StringInputFilterDashboardItemComponent.getDefaultState();
            this.setupModelFromState(state);
            // this.cd.detectChanges();
        }
    };

    static getDefaultState(): IStringInputFilterDatasourceColumnState {
        return {
            value: '',
            isFilterApplied: false,
            isSplitApplied: false,
        };
    }

    /**
     * Setup model from state
     * @param state - State of the column
     * @throws - Error if state is undefined
     */
    private setupModelFromState(
        state: IStringInputFilterDatasourceColumnState
    ) {
        if (!state) {
            throw new Error('State is undefined');
        }
        if (state.isFilterApplied) {
            if (!state.value) {
                this.logger.error('Filter is applied but value is undefined');
                throw new Error('Filter is applied but value is undefined');
            }
            this.value = state.value;
        }
    }

    public onModelChange(newValue: string) {
        this.logger.debug('onModelChange', newValue);
        if (newValue === '') {
            this.state =
                StringInputFilterDashboardItemComponent.getDefaultState();
        } else {
            this.state = {
                ...this.state,
                value: newValue,
                isFilterApplied: !!newValue,
            };
        }
        this.emitState();
    }

    emitState() {
        this.state = {
            ...this.state,
        };
        console.debug({
            msg: `StringInputFilterDashboardItemComponent.${this.dashboardItem.datasourceColumnId}.emitState`,
            filter: JSON.stringify({
                dimensionId: this.dashboardItem.datasourceColumnId,
                state: this.state,
            }),
        });
        const change: IQueryContextChange = {
            datasourceColumn: {
                id: this.dashboardItem.datasourceColumnId,
                state: this.state,
            },
        };
        // this.change.emit(change);
        this.queryContextService.changePendingQueryContext(change);
    }

    /**
     * Compiles SQL tokens for a MultipleSelect component.
     * @param columnSQL sql of a column
     * @param state state of a column
     * @returns
     */
    static compileSqlTokens(
        columnSQL: string,
        state: IQueryContextDatasourceColumnState,
        toSql: (
            columnSQL: string,
            state: IStringInputFilterDatasourceColumnState
        ) => string = StringInputFilterDashboardItemComponent.getDefaultWhereSQL
    ): Partial<ICompiledSQLTokens> {
        const split_group: Partial<ICompiledSQLTokens> = (() => {
            if (state.isSplitApplied)
                return {
                    split_group: [columnSQL],
                };
            return {};
        })();
        const where: Partial<ICompiledSQLTokens> = (() => {
            if (state.isFilterApplied)
                return {
                    where: [toSql(columnSQL, state)],
                };
            return {};
        })();
        return { ...split_group, ...where };
    }

    static getDefaultWhereSQL(
        columnName: string,
        state: IStringInputFilterDatasourceColumnState
    ): string {
        return `CONTAINS_SUBSTR(${columnName}, '${state.value}')`;
    }
}
